// Programmatic SEO landing pages.
// SAFE FRAMING: never names a partnered builder in URL or H1, never compares partners.
// Pages educate buyers about builder TYPES, BUDGETS, STYLES, and AREAS - and route
// them into the BuildPilot advisory funnel for matched recommendations.

const REGIONS = {
  'northern-adelaide': {
    label: 'Northern Adelaide',
    description: "Adelaide's fastest-growing corridor with new estates at Riverlea, Angle Vale, Two Wells and Munno Para. Known for competitive entry-level pricing and strong builder availability.",
    suburbs: ['Riverlea', 'Angle Vale', 'Two Wells', 'Munno Para', 'Andrews Farm', 'Eyre', 'Gawler'],
    landRange: '$220,000 to $300,000',
  },
  'southern-adelaide': {
    label: 'Southern Adelaide',
    description: 'Coastal lifestyle areas around Aldinga, Seaford, Sellicks Beach and Hackham. Strong family appeal with proximity to beaches and McLaren Vale.',
    suburbs: ['Seaford', 'Aldinga', 'Sellicks Beach', 'McLaren Vale', 'Hackham', 'Morphett Vale'],
    landRange: '$280,000 to $400,000',
  },
  'adelaide-hills': {
    label: 'Adelaide Hills',
    description: 'Lifestyle living in Mount Barker, Stirling, Hahndorf and surrounds. Site costs vary significantly with slope and soil class.',
    suburbs: ['Mount Barker', 'Stirling', 'Hahndorf', 'Bridgewater', 'Aldgate', 'Nairne'],
    landRange: '$220,000 to $400,000',
  },
  'western-adelaide': {
    label: 'Western Adelaide',
    description: 'Established coastal suburbs from Henley Beach down to Glenelg. Knockdown rebuild dominates as vacant land is rare.',
    suburbs: ['Henley Beach', 'Glenelg', 'West Beach', 'Fulham', 'Plympton', 'Lockleys'],
    landRange: '$500,000 to $1.5M+ (KDR market)',
  },
};

const BUDGETS = {
  'under-400k': {
    label: 'under $400,000',
    minK: 0,
    maxK: 400,
    realityCheck:
      "Adelaide finished house and land packages start from around $650,000 in 2026. A build budget of under $400,000 generally means a smaller volume home (around 180 to 200sqm) on a finished block, with land purchased separately. Cost figures are guidance only and should be confirmed with builders directly. (Source: ABS, RevenueSA)",
    fitsWith: 'Volume builders, simple single-storey designs, standard inclusions',
  },
  'under-500k': {
    label: 'under $500,000',
    minK: 400,
    maxK: 500,
    realityCheck:
      'A build budget of $400,000 to $500,000 covers most volume builder 4-bedroom designs in Adelaide. Once land and statutory costs are added, total spend on a finished package typically reaches $650,000 to $750,000. Cost figures are guidance only.',
    fitsWith: 'Volume builders, well-specified 4-bedroom homes, popular floor plans',
  },
  'under-600k': {
    label: 'under $600,000',
    minK: 500,
    maxK: 600,
    realityCheck:
      'A $500,000 to $600,000 build budget gives you access to mid-range volume and project builders with upgraded inclusions and larger floor plans. Total finished spend with land typically lands $750,000 to $850,000. Cost figures are guidance only.',
    fitsWith: 'Mid-range volume and project builders, premium inclusion packages',
  },
  'under-800k': {
    label: 'under $800,000',
    minK: 600,
    maxK: 800,
    realityCheck:
      'A $600,000 to $800,000 build budget opens up project builders, semi-custom designs, and premium volume range homes with high-end inclusions. Cost figures are guidance only and should be confirmed in writing with builders.',
    fitsWith: 'Project builders, semi-custom designs, premium inclusions',
  },
};

const HOME_TYPES = {
  'single-storey': {
    label: 'Single Storey Home',
    description: 'Single-storey homes are the most common Adelaide build. They suit retirees, young families, and anyone who wants single-level living without stairs.',
    keyConsiderations: [
      'Block size matters more for single-storey than double',
      'Site costs are usually lower than double-storey',
      'Roof and slab are larger so material costs are spread differently',
    ],
  },
  'double-storey': {
    label: 'Double Storey Home',
    description: 'Double-storey homes maximise floor space on smaller blocks. Increasingly common in inner-ring suburbs and infill developments.',
    keyConsiderations: [
      'Engineering and footings are more involved than single-storey',
      'Stair design eats into your floor plan, factor this in',
      'Some builders specialise in double-storey, others rarely do them',
    ],
  },
  'family-home': {
    label: 'Family Home',
    description: 'Family-focused homes typically have 4 bedrooms, 2 living zones, a study or activity space, and a layout that separates parents and kids.',
    keyConsiderations: [
      'Theatre or rumpus room adds value but pushes up base price',
      '4 bedroom + study layouts are increasingly common',
      'Kitchen and pantry placement matters a lot for daily flow',
    ],
  },
  'first-home': {
    label: 'First Home',
    description: 'First home buyer focused designs balance affordability, FHOG eligibility, and future resale appeal.',
    keyConsiderations: [
      'Stay under the $650,000 finished price for full SA stamp duty exemption',
      'Make sure the contract is FHOG-eligible (new build, primary residence)',
      "Don't over-spec inclusions, save for future upgrades",
    ],
  },
};

// Page metadata - generated programmatically
export const programmaticPages = [];

// Budget × Region pages
Object.entries(BUDGETS).forEach(([budgetSlug, budget]) => {
  Object.entries(REGIONS).forEach(([regionSlug, region]) => {
    programmaticPages.push({
      slug: `home-builders-${budgetSlug}-${regionSlug}`,
      type: 'budget-region',
      h1: `Home Builders ${budget.label} in ${region.label}`,
      metaTitle: `Home Builders ${budget.label} in ${region.label} (2026 Guide) | BuildPilot`,
      metaDescription: `Independent guide to building ${budget.label} in ${region.label}. Realistic costs, what fits the budget, suburbs to consider, and how to shortlist builders. ${region.label}: ${region.suburbs.slice(0, 3).join(', ')} and more.`,
      region,
      budget,
      keywords: [
        `home builders ${budget.label} ${region.label.toLowerCase()}`,
        `cheap home builders ${region.label.toLowerCase()}`,
        `affordable builders ${region.label.toLowerCase()}`,
        `building a house ${budget.label}`,
      ],
    });
  });
});

// Home Type × Region pages
Object.entries(HOME_TYPES).forEach(([typeSlug, homeType]) => {
  Object.entries(REGIONS).forEach(([regionSlug, region]) => {
    programmaticPages.push({
      slug: `${typeSlug}-builders-${regionSlug}`,
      type: 'type-region',
      h1: `${homeType.label} Builders in ${region.label}`,
      metaTitle: `${homeType.label} Builders ${region.label} (2026) | BuildPilot`,
      metaDescription: `Building a ${homeType.label.toLowerCase()} in ${region.label}? Independent guide to design considerations, typical costs, suburbs to consider, and how to find the right builder for your project.`,
      region,
      homeType,
      keywords: [
        `${homeType.label.toLowerCase()} builders ${region.label.toLowerCase()}`,
        `${typeSlug} home builders adelaide`,
        `best ${typeSlug} builder ${region.label.toLowerCase()}`,
      ],
    });
  });
});

export const getProgrammaticPage = (slug) =>
  programmaticPages.find((p) => p.slug === slug) || null;

export const getAllProgrammaticSlugs = () => programmaticPages.map((p) => p.slug);

export { REGIONS, BUDGETS, HOME_TYPES };
